import { useContext, useEffect, useMemo } from 'react';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { LoadingState } from 'utils/loadingState';
import { AuthContext } from '@urbanx/agx.auth.client';
import {
  fetchMarketingItemTemplates,
  fetchMarketingPackageTemplates,
} from '../../marketing/marketingReducer';
import {
  fetchRealEstate,
  reloadRealEstate,
} from '../../realEstate/realEstateReducer';
import useFormAddressSelector from 'selectors/useFormAddressSelector';
import useFormPropertyDetailsSelector from 'selectors/useFormPropertyDetailsSelector';
import { ListingType } from 'Api/RealEstate/Types/types';
import { ExternalIntegrations } from '@urbanx/agx.agencies.client';
import useGetPropertyDomainPrice from 'features/domain/useGetPropertyDomainPrice';

const getRealEstateListingType = (
  propertyType?: string | null
): ListingType => {
  switch (propertyType) {
    case 'House':
    case 'Unit/Apartment':
    case 'Townhouse':
    case 'Villa':
      return ListingType.Residential;
    case 'Residential Land':
      return ListingType.Land;
    case 'Rural':
      return ListingType.Rural;
    default:
      return ListingType.Residential;
  }
};

const MarketingPackagesPreloader = () => {
  const { getAuthToken } = useContext(AuthContext);
  const dispatch = useAppDispatch();
  const propertyDetails = useFormPropertyDetailsSelector();
  const { loadingState: realEstateLoadingState, key: realEstateKey } =
    useAppSelector(state => state.realEstate);
  const {
    agencyInfo: { info },
  } = useAppSelector(state => state.agencies);
  const {
    marketingPackages: { loadingState: marketingPackagesLoadingState },
    marketingItems: { loadingState: marketingItemsLoadingState },
  } = useAppSelector(state => state.marketing);
  const { campaignId } = useAppSelector(state => state.form);
  const externalIntegrations = info?.externalIntegrations;

  const realEstateIntegrationEnabled = useMemo(() => {
    if (!externalIntegrations) return false;

    return externalIntegrations.includes(ExternalIntegrations.RealEstateDotCom);
  }, [externalIntegrations]);

  const propertyAddress = useFormAddressSelector();

  useGetPropertyDomainPrice();

  const listingType = useMemo(() => {
    return getRealEstateListingType(propertyDetails?.propertyType);
  }, [propertyDetails?.propertyType]);

  useEffect(() => {
    if (propertyAddress == null) return;

    const generatedRealEstateKey = `${propertyAddress.suburb}|${propertyAddress.state}|${propertyAddress.postalCode}|${listingType}`;

    if (realEstateKey !== generatedRealEstateKey) {
      dispatch(reloadRealEstate());
    }
  }, [propertyAddress, listingType]);

  useEffect(() => {
    if (
      !realEstateIntegrationEnabled ||
      realEstateLoadingState !== LoadingState.NotLoaded ||
      !propertyAddress?.suburb ||
      !propertyAddress?.state ||
      !propertyAddress?.postalCode
    )
      return;

    getAuthToken().then(token => {
      if (!token || !campaignId) return;

      dispatch(
        fetchRealEstate(token, {
          campaignId,
          suburb: propertyAddress.suburb,
          state: propertyAddress.state,
          postcode: propertyAddress.postalCode,
          country: 'Australia',
          listingType,
        })
      );
    });
  }, [realEstateLoadingState, propertyAddress, campaignId, listingType]);

  useEffect(() => {
    if (marketingItemsLoadingState !== LoadingState.NotLoaded) return;

    getAuthToken().then(token => {
      if (!token) return;
      dispatch(fetchMarketingItemTemplates(token));
    });
  }, [marketingItemsLoadingState]);

  useEffect(() => {
    if (marketingPackagesLoadingState !== LoadingState.NotLoaded) return;

    getAuthToken().then(token => {
      if (!token) return;
      dispatch(fetchMarketingPackageTemplates(token));
    });
  }, [marketingPackagesLoadingState]);

  return <></>;
};

export default MarketingPackagesPreloader;
